// treeLayout.js (web app)
// Uses Firestore timestamp format (_seconds / seconds) since members come from Firestore

export function buildTreeLayout(members) {
  if (!members || members.length === 0) return {};

  // Group by generation
  const byGeneration = {};
  members.forEach(member => {
    const gen = member.generation;
    if (!byGeneration[gen]) byGeneration[gen] = [];
    byGeneration[gen].push(member);
  });

  // Helper: get createdAt as a number (Firestore format)
  const getTime = (m) =>
    m.createdAt?._seconds ?? m.createdAt?.seconds ?? 0;

  // Sort each generation
  const sortedGens = Object.keys(byGeneration).map(Number).sort((a, b) => a - b);

  // Track sorted order of each generation for parent-position lookup
  const sortedOrderMap = {}; // gen → { memberId → index }

  sortedGens.forEach(gen => {
    const genMembers = byGeneration[gen];

    if (gen <= 0) {
      // Generation 0 and above: sort by createdAt only
      genMembers.sort((a, b) => getTime(a) - getTime(b));

    } else {
      // Child generations (1, 2, 3...):
      // Step 1: Separate into anchored (have parentIds linking to gen above) and unanchored
      const parentGenOrder = sortedOrderMap[gen - 1] || {};

      const anchored = [];
      const unanchored = [];

      genMembers.forEach(m => {
        const hasParents = m.parentIds && m.parentIds.length > 0 &&
          m.parentIds.some(id => parentGenOrder[id] !== undefined);
        if (hasParents) {
          anchored.push(m);
        } else {
          unanchored.push(m);
        }
      });

      // Step 2: Sort anchored members by parent position then createdAt
      anchored.sort((a, b) => {
        const aPositions = (a.parentIds || [])
          .map(id => parentGenOrder[id])
          .filter(pos => pos !== undefined);
        const bPositions = (b.parentIds || [])
          .map(id => parentGenOrder[id])
          .filter(pos => pos !== undefined);

        const aMin = aPositions.length > 0 ? Math.min(...aPositions) : Infinity;
        const bMin = bPositions.length > 0 ? Math.min(...bPositions) : Infinity;

        if (aMin !== bMin) return aMin - bMin;
        return getTime(a) - getTime(b);
      });

      // Step 3: Insert spouses immediately after their partner
      // Spouses have no parentIds — they are unanchored but belong next to their partner
      const result = [...anchored];
      const inserted = new Set();

      for (let i = result.length - 1; i >= 0; i--) {
        const current = result[i];
        const spousesOfCurrent = unanchored.filter(m =>
          !inserted.has(m.memberId) &&
          (m.spouseId === current.memberId || m.spouse2Id === current.memberId)
        );
        if (spousesOfCurrent.length > 0) {
          spousesOfCurrent.sort((a, b) => getTime(a) - getTime(b));
          result.splice(i + 1, 0, ...spousesOfCurrent);
          spousesOfCurrent.forEach(s => inserted.add(s.memberId));
        }
      }

      // Step 4: Append remaining unanchored (no spouse in result) sorted by createdAt
      const remaining = unanchored
        .filter(m => !inserted.has(m.memberId))
        .sort((a, b) => getTime(a) - getTime(b));

      result.push(...remaining);

      byGeneration[gen] = result;
    }

    // Record sorted order for next generation
    sortedOrderMap[gen] = {};
    byGeneration[gen].forEach((m, idx) => {
      sortedOrderMap[gen][m.memberId] = idx;
    });
  });

  return byGeneration;
}

export function getGenerationLabel(gen) {
  const labels = {
    '-4': 'Great-Great Grandparents',
    '-3': 'Great Grandparents',
    '-2': 'Grandparents',
    '-1': 'Parents',
    '0': 'Your Generation',
    '1': 'Children',
    '2': 'Grandchildren',
    '3': 'Great Grandchildren',
  };
  return labels[String(gen)] || `Generation ${gen}`;
}

export function getCellColour(member, isCreator) {
  if (member.isDeceased) {
    return { bg: '#E5E7EB', border: '#9CA3AF', text: '#6B7280' };
  }
  if (isCreator) {
    return { bg: '#DBEAFE', border: '#D4A017', text: '#1e3a5f' };
  }
  if (member.gender === 'male') {
    return { bg: '#DBEAFE', border: '#93C5FD', text: '#1e3a5f' };
  }
  if (member.gender === 'female') {
    return { bg: '#FCE7F3', border: '#F9A8D4', text: '#6b2150' };
  }
  return { bg: '#F3F4F6', border: '#D1D5DB', text: '#374151' };
}

const GROUP_COLORS = [
  '#1D9E75', '#D85A30', '#378ADD', '#D4537E',
  '#639922', '#7F77DD', '#BA7517', '#04342C',
  '#993556', '#0C447C', '#993C1D', '#26215C',
  '#085041', '#712B13', '#4B1528', '#412402',
];

function canonicalGroupKey(idA, idB) {
  return [idA, idB].filter(Boolean).sort().join('|') || null;
}

export function buildGroupColorMap(allMembers) {
  const map = {};
  if (!allMembers || allMembers.length === 0) return map;

  const sorted = [...allMembers].sort((a, b) => {
    if (a.generation !== b.generation) return a.generation - b.generation;
    const aTime = a.createdAt?._seconds ?? a.createdAt?.seconds ?? 0;
    const bTime = b.createdAt?._seconds ?? b.createdAt?.seconds ?? 0;
    return aTime - bTime;
  });

  let nextColorIndex = 0;

  const assign = (key) => {
    if (!key || map[key]) return;
    map[key] = GROUP_COLORS[nextColorIndex % GROUP_COLORS.length];
    nextColorIndex += 1;
  };

  sorted.forEach(member => {
    if (member.parentIds && member.parentIds.length > 0) {
      const [a, b] = member.parentIds;
      assign(canonicalGroupKey(a, b));
    }
  });

  return map;
}

export function getLeftStripColor(member, groupColorMap) {
  if (!member.parentIds || member.parentIds.length === 0) return null;
  const [a, b] = member.parentIds;
  const key = canonicalGroupKey(a, b);
  return (key && groupColorMap?.[key]) || null;
}

export function getRightStripColor(member, allMembers, groupColorMap) {
  const children = allMembers.filter(m =>
    m.parentIds && m.parentIds.includes(member.memberId)
  );
  if (children.length === 0) return null;

  const firstChild = children[0];
  const [a, b] = firstChild.parentIds;
  const key = canonicalGroupKey(a, b);
  return (key && groupColorMap?.[key]) || null;
}